
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { SocialEcosystem } from '../components/shared/SocialEcosystem';
import { RoadmapSection } from '../components/shared/RoadmapSection';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  Copy,
  ExternalLink,
  FileText,
  Zap,
  Lock,
  Coins
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ROUTES, EXTERNAL_LINKS, ASSETS, DOCUMENTS } from '../constants';

export const TokenPage: React.FC = () => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  // Distribution Data
  // Using translated labels from t('token.distribution.items')
  const distributionItems = t('token.distribution.items') as any[];

  const distributionData = [
    { name: distributionItems[0]?.label || 'Corporate Treasury', value: 38, color: '#10B981' }, // Emerald 500
    { name: distributionItems[1]?.label || 'CEX Listing', value: 15, color: '#2DD4BF' }, // Teal 400
    { name: distributionItems[2]?.label || 'Founders', value: 12, color: '#F97316' }, // Orange 500
    { name: distributionItems[3]?.label || '2nd Airdrop', value: 10, color: '#D946EF' }, // Fuchsia 500
    { name: distributionItems[4]?.label || '3rd Airdrop', value: 10, color: '#8B5CF6' }, // Violet 500
    { name: distributionItems[5]?.label || 'Liquidity Pool', value: 9, color: '#3B82F6' }, // Blue 500
    { name: distributionItems[6]?.label || '1st Airdrop', value: 4, color: '#6366F1' }, // Indigo 500
    { name: distributionItems[7]?.label || 'Private Sale', value: 2, color: '#FACC15' }, // Yellow 400
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText('CTCNkcqpbxMTTRdvAyhzhUUSLbYLBFEnDgyr5zs4hWxX');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-[#0B1120] text-white min-h-screen">

      {/* 1. HERO */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex mb-6">
            <span className="bg-[#151E32] text-orange-500 text-[10px] sm:text-xs font-bold px-3 py-2 rounded-full uppercase tracking-wider border border-gray-800 flex items-center text-center leading-tight max-w-[90vw]">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse flex-shrink-0"></span>
              {t('token.hero.badge')}
            </span>
          </div>

          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-1/2">
              <AnimatedSection>
                <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight">
                  {t('token.hero.titlePart1')} <span className="text-orange-500">{t('token.hero.titlePart2')}</span>
                </h1>
                <p className="text-gray-400 text-lg mb-10 leading-relaxed max-w-xl">
                  {t('token.hero.description')}
                </p>

                <div className="flex flex-wrap gap-4 mb-12">
                  <a href={EXTERNAL_LINKS.RAYDIUM_SWAP} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white border-0 font-bold px-8 py-3 rounded-lg shadow-lg shadow-orange-500/20">
                      <Zap className="mr-2 w-5 h-5 fill-current" />
                      {t('token.hero.buyBtn')}
                    </Button>
                  </a>
                  <Link to={ROUTES.WHITEPAPER}>
                    <Button className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-orange-600/20">
                      {t('token.hero.whitepaperBtn')}
                    </Button>
                  </Link>
                </div>

                <div className="flex flex-wrap gap-6 text-xs text-gray-500 font-medium">
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    {t('token.hero.check1')}
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    {t('token.hero.check2')}
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    {t('token.hero.check3')}
                  </div>
                </div>
              </AnimatedSection>
            </div>

            <div className="lg:w-1/2 w-full">
              <AnimatedSection delay={200}>
                <div className="bg-[#151E32] border border-gray-800 rounded-2xl p-8 relative overflow-hidden shadow-2xl">
                  {/* Logo TOKEN watermark - trasparente */}
                  <img
                    src={ASSETS.LOGO_TOKEN}
                    className="absolute -top-10 -right-10 w-64 h-64 opacity-[0.08] pointer-events-none object-contain z-30 animate-pulse"
                    alt="CTC Token Background"
                  />

                  <div className="flex justify-between items-start mb-8 relative z-10">
                    <div>
                      <h3 className="text-2xl font-bold text-white">{t('token.hero.card.title')}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t('token.hero.card.subtitle')}</p>
                    </div>
                  </div>

                  <div className="space-y-6 relative z-10">
                    <div className="bg-[#0B1120] p-4 rounded-xl border border-gray-800/50">
                      <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">
                        {t('token.hero.card.item1Title')}
                      </h4>
                      <p className="text-sm text-gray-300">
                        {t('token.hero.card.item1Desc')}
                      </p>
                    </div>
                    <div className="bg-[#0B1120] p-4 rounded-xl border border-gray-800/50">
                      <h4 className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-2">
                        {t('token.hero.card.item2Title')}
                      </h4>
                      <p className="text-sm text-gray-300">
                        {t('token.hero.card.item2Desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CONTRACT ADDRESS */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <AnimatedSection className="md:col-span-3">
              <div className="bg-[#151E32] border border-gray-800 rounded-xl p-6">
                <label className="block text-xs font-bold text-gray-400 uppercase mb-3 ml-1">
                  {t('token.contract.addressLabel')}
                </label>
                <div className="bg-[#0B1120] border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-4">
                  <code className="text-sm text-blue-400 font-mono truncate">
                    CTCNkcqpbxMTTRdvAyhzhUUSLbYLBFEnDgyr5zs4hWxX
                  </code>
                </div>
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={handleCopy}
                    className="flex items-center text-xs font-bold text-gray-400 hover:text-white transition-colors bg-[#0B1120] px-4 py-2 rounded border border-gray-700 hover:border-gray-500"
                  >
                    <Copy size={14} className="mr-2" />
                    {copied ? t('common.copied') : t('token.contract.copyBtn')}
                  </button>
                  <a
                    href="https://solscan.io/token/CTCNkcqpbxMTTRdvAyhzhUUSLbYLBFEnDgyr5zs4hWxX"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center text-xs font-bold text-orange-500 hover:text-orange-400 transition-colors bg-[#0B1120] px-4 py-2 rounded border border-orange-500/30 hover:border-orange-500/60"
                  >
                    <ExternalLink size={14} className="mr-2" />
                    {t('token.contract.solscanBtn')}
                  </a>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100} className="md:col-span-2">
              <div className="bg-[#151E32] border border-gray-800 rounded-xl p-6 h-full flex flex-col justify-center">
                <label className="block text-xs font-bold text-gray-400 uppercase mb-3 ml-1">
                  {t('token.contract.dtiLabel')}
                </label>
                <div className="bg-[#0B1120] border border-gray-700 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">{t('token.contract.dtiIdLabel')}</p>
                  <p className="text-lg font-mono font-bold text-white tracking-widest">SRBG66MR9</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 3. UTILITY & BENEFITS */}
      <section className="py-24 bg-[#080C14] border-y border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              {t('token.utility.titlePart1')} <span className="text-orange-500">{t('token.utility.titlePart2')}</span>
            </h2>
            <span className="inline-block bg-green-500/10 text-green-500 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-green-500/20">
              <Zap size={10} className="inline mr-1" /> {t('token.utility.comingSoon')}
            </span>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(t('token.utility.cards') as any[]).map((card: any, i: number) => (
              <AnimatedSection key={i} delay={i * 150}>
                <div className="bg-[#151E32] border border-gray-800 p-8 rounded-xl h-full flex flex-col items-start relative overflow-hidden group">
                  <div className="w-12 h-12 bg-[#0B1120] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform border border-gray-700">
                    {i === 0 ? <Coins className="text-blue-500" /> : <Lock className="text-orange-500" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{card.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 4. DISTRIBUTION */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wide">
              {t('token.distribution.titlePart1')} <span className="text-orange-500">{t('token.distribution.titlePart2')}</span>
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {t('token.distribution.description')}
            </p>
          </AnimatedSection>

          <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-8 md:p-12">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="lg:w-1/3 w-full flex flex-col items-center justify-center relative">
                <div className="w-full max-w-[300px] aspect-square relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={distributionData}
                        innerRadius={85}
                        outerRadius={125}
                        paddingAngle={4}
                        cornerRadius={6}
                        dataKey="value"
                        stroke="none"
                      >
                        {distributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '12px', color: '#fff', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)' }}
                        itemStyle={{ color: '#fff', fontWeight: 'bold' }}
                        formatter={(value) => `${value}%`}
                        cursor={false}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <h3 className="text-4xl font-black text-white drop-shadow-md">CTC</h3>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">Token</span>
                  </div>
                </div>
              </div>

              <div className="lg:w-2/3 w-full">
                <div className="space-y-4">
                  {(t('token.distribution.items') as any[]).map((item: any, idx: number) => {
                    const dataItem = distributionData[idx] || distributionData[0];
                    return (
                      <div key={idx} className="group">
                        <div className="flex items-start">
                          <div
                            className="w-3 h-3 rounded-full mt-1.5 mr-4 flex-shrink-0"
                            style={{ backgroundColor: dataItem.color }}
                          ></div>
                          <div className="flex-1">
                            <div className="flex flex-wrap justify-between items-baseline mb-1">
                              <h4 className="text-sm font-bold text-white mr-2">{item.label}</h4>
                              <div className="text-right">
                                <span className="text-sm font-bold" style={{ color: dataItem.color }}>{item.percentage}</span>
                                <span className="text-xs text-gray-500 ml-2 block sm:inline">{item.amount}</span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 leading-relaxed group-hover:text-gray-400 transition-colors">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                        {idx < distributionData.length - 1 && <div className="border-b border-gray-800 my-4 ml-7"></div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. ROADMAP TOKEN */}
      <RoadmapSection category="token" showFullLink={true} />

      {/* 6. WHITEPAPER & COMPLIANCE */}
      <section className="py-24 bg-[#0B1120]" id="whitepaper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <FileText className="w-12 h-12 text-blue-500 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-wide">
              {t('token.whitepaper.titlePart1')} <span className="text-orange-500">{t('token.whitepaper.titlePart2')}</span>
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed max-w-2xl mx-auto">
              {t('token.whitepaper.description')}
            </p>
            <a href={DOCUMENTS.WHITEPAPER_LATEST} download>
              <Button className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-orange-600/20">
                {t('token.whitepaper.downloadBtn')}
              </Button>
            </a>
          </AnimatedSection>

          {/* Compliance Checks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {(t('token.compliance.checks') as string[]).map((check: string, i: number) => (
              <div key={i} className="bg-[#151E32] p-6 rounded-lg border border-gray-800 flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 mb-4">
                  <CheckCircle size={20} />
                </div>
                <span className="font-bold text-white text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SOCIAL */}
      <section className="py-20 bg-[#080C14] border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <SocialEcosystem />
          </AnimatedSection>
        </div>
      </section>



    </div>
  );
};
